/// <amd-module name="Test/TrendChartView/Actions/TrendChartActionsCreator" />
import { AnnouncementActions } from "Test/Analytics/Common/Actions/AnnouncementActions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import * as TcmPerfScenarios from "Test/Analytics/Common/Utils/TCMPerfScenarios";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { ITrendChartData, ITrendChartSource } from "Test/Analytics/TrendChartCommon/Types";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { TrendChartSourceService } from "Test/FilteringLogic/Services/TrendChartSourceService";
import { TrendChartActions } from "Test/TrendChartView/Actions/TrendChartActions";
import * as Resources from "Test/TrendChartView/Resources";
import { TrendChartDefaultSource } from "Test/TrendChartView/Sources/TrendChartDefaultSource";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export class TrendChartActionsCreator {
    private _defaultChartsSource: TrendChartDefaultSource;
    private _perfService: IVssPerformanceService;
    constructor(private _actions: TrendChartActions, private _announcementActions: AnnouncementActions, private _pageContext: IVssPageContext) {
        this._defaultChartsSource = new TrendChartDefaultSource(_pageContext);
        this._perfService = _pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public updateConfigurationValues(newConfValues: CommonTypes.IReportConfiguration): void {
        this._actions.updateConfigurationValuesAction.invoke(newConfValues);
    }

    public updateTrendChartMetric(metric: CommonTypes.Metric): void {
        this._actions.updateTrendChartMetricAction.invoke(metric);
    }

    public updateTrendChart(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        chartMetric: CommonTypes.Metric,
        loadType: CommonTypes.LoadType
    ): Promise<void> {
        this._perfService.startScenario(TcmPerfScenarios.TestAX_AggregateTrendChart, false);

        const filtersApplied: string[] = confValues.configuredFilters ? Object.keys(confValues.configuredFilters) : [];
        const telemetryData: { [key: string]: string | number } = {
            [TcmTelemetryConstants.ChartType]: CommonTypes.Metric[chartMetric].toString(),
            [TcmTelemetryConstants.GroupBy]: CommonTypes.GroupBy[confValues.groupBy].toString(),
            [TcmTelemetryConstants.Duration]: CommonTypes.Period[confValues.period].toString(),
            [TcmTelemetryConstants.LoadType]: CommonTypes.LoadType[loadType].toString(),
            [TcmTelemetryConstants.Filters]: filtersApplied.length ? filtersApplied.toString() : TcmTelemetryConstants.Clear,
            [TcmTelemetryConstants.FiltersCount]: filtersApplied.length
        };

        const defaultConfValues: CommonTypes.IReportConfiguration = {
            groupBy: CommonTypes.GroupBy.None,
            trendBy: CommonTypes.TrendBy.Days,
            period: CommonTypes.Period.Days_14,
            outcomes: [CommonTypes.TestOutcome.Failed],
            configuredFilters: {
                [CommonTypes.Filter.Workflow.toString()]: {
                    values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
                }
            }
        };

        if (ConfigurationUtility.areConfValuesEqual(defaultConfValues, confValues) && CommonTypes.Metric.ResultCountAndPassRate == chartMetric) {
            return this._defaultChartsSource.queryDefaultTrendChartData(testReportContext).then(
                (chartData: ITrendChartData) => {
                    this._actions.updateTrendChartAction.invoke(chartData);
                    this._announcementActions.announceAction.invoke(
                        Utils_String.localeFormat(Resources.TrendChartLoaded, CommonTypes.Metric[chartMetric])
                    );

                    this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_AggregateTrendChart, false);

                    telemetryData[TcmTelemetryConstants.IsCached] = String(chartData.isCachedData);

                    publishEvent(
                        this._pageContext,
                        TcmTelemetryConstants.featureTestAX_AggregateTrendChart,
                        telemetryData,
                        testReportContext,
                        confValues
                    );
                },
                (error: Error) => {
                    this._actions.onError.invoke(error);
                    this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_AggregateTrendChart, false, undefined, {
                        status: "error"
                    });
                }
            );
        } else {
            return this._getTrendChartSourceManager()
                .then((trendChartSource: ITrendChartSource) => {
                    trendChartSource.queryTrendChartData(testReportContext, confValues, chartMetric).then(
                        (chartData: ITrendChartData) => {
                            this._actions.updateTrendChartAction.invoke(chartData);
                            this._announcementActions.announceAction.invoke(
                                Utils_String.localeFormat(Resources.TrendChartLoaded, CommonTypes.Metric[chartMetric])
                            );

                            this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_AggregateTrendChart, false);

                            telemetryData[TcmTelemetryConstants.IsCached] = String(chartData.isCachedData);

                            publishEvent(
                                this._pageContext,
                                TcmTelemetryConstants.featureTestAX_AggregateTrendChart,
                                telemetryData,
                                testReportContext,
                                confValues
                            );
                        },
                        (error: Error) => {
                            this._actions.onError.invoke(error);
                            this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_AggregateTrendChart, false, undefined, {
                                status: "error"
                            });
                        }
                    );
                })
                .catch((reason: any) => {
                    this._actions.onError.invoke(reason);
                });
        }
    }

    private async _getTrendChartSourceManager(): Promise<ITrendChartSource> {
        const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        await contributionService.getContributionAsync("ms.vss-test-analytics-web.filtering-logic");

        const trendChartSourceManager = this._pageContext.getService<TrendChartSourceService>("TrendChartSourceService");
        if (trendChartSourceManager) {
            trendChartSourceManager.initialize("TrendChartSourceService", this._pageContext);
        }
        return trendChartSourceManager;
    }
}
